import { useEffect, useRef, useState } from "react";
import { api } from "./lib/api";

export type TemplateBundles = Awaited<ReturnType<typeof api.templates>>;
type PublishedOption = { template: TemplateBundles[number]["template"]; version: TemplateBundles[number]["versions"][number] };

export function publishedPreparationOptions(templates: TemplateBundles): PublishedOption[] {
  return templates.flatMap((bundle) => bundle.versions
    .filter((version) => version.status === "published")
    .map((version) => ({ template: bundle.template, version })))
    .sort((left, right) => {
      const leftTime = Date.parse(left.version.publishedAt ?? left.version.createdAt ?? "");
      const rightTime = Date.parse(right.version.publishedAt ?? right.version.createdAt ?? "");
      if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) return rightTime - leftTime;
      if (left.template.id === right.template.id && left.version.number !== right.version.number) return right.version.number - left.version.number;
      if (left.template.latestVersion !== right.template.latestVersion) return right.template.latestVersion - left.template.latestVersion;
      return left.template.name.localeCompare(right.template.name);
    });
}

export interface PreparationSigner {
  id: string;
  name: string;
  email: string;
  role: string;
}

export function parseRecipientCsv(text: string, expectedSigners: readonly PreparationSigner[]): PreparationSigner[] {
  const rows = parseCsvRows(text.replace(/^\uFEFF/, "")).filter((row) => row.some((cell) => cell.trim()));
  if (rows.length < 2) throw new Error("The CSV must include a header and at least one recipient.");
  const headers = rows[0]!.map((header) => header.trim().toLowerCase());
  const roleColumn = headers.indexOf("role");
  const nameColumn = headers.indexOf("name");
  const emailColumn = headers.indexOf("email");
  if ([roleColumn, nameColumn, emailColumn].some((column) => column < 0)) throw new Error("CSV headers must include role, name, and email.");
  const imported = new Map<string, { name: string; email: string }>();
  for (const row of rows.slice(1)) {
    const role = (row[roleColumn] ?? "").trim();
    const name = (row[nameColumn] ?? "").trim();
    const email = (row[emailColumn] ?? "").trim();
    if (!role || !name || !email) throw new Error("Every CSV recipient requires a role, name, and email.");
    const roleKey = role.toLowerCase();
    if (imported.has(roleKey)) throw new Error(`The CSV contains more than one recipient for role ${role}.`);
    imported.set(roleKey, { name, email });
  }
  const expectedRoles = new Set(expectedSigners.map((signer) => signer.role.toLowerCase()));
  const unknownRole = [...imported.keys()].find((role) => !expectedRoles.has(role));
  if (unknownRole) throw new Error(`The CSV role ${unknownRole} is not defined by this template.`);
  const missingRole = expectedSigners.find((signer) => !imported.has(signer.role.toLowerCase()));
  if (missingRole) throw new Error(`The CSV is missing the template role ${missingRole.role}.`);
  return expectedSigners.map((signer) => ({ ...signer, ...imported.get(signer.role.toLowerCase())! }));
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]!;
    if (character === '"') {
      if (quoted && text[index + 1] === '"') { cell += '"'; index += 1; }
      else quoted = !quoted;
    } else if (character === "," && !quoted) {
      row.push(cell); cell = "";
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      row.push(cell); rows.push(row); row = []; cell = "";
    } else cell += character;
  }
  if (quoted) throw new Error("The CSV contains an unclosed quoted value.");
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

type PublishedVersion = TemplateBundles[number]["versions"][number];

export function preparationSignersForVersion(version: PublishedVersion): PreparationSigner[] {
  const configuredRoles = version.routingPolicy?.signerRoles?.filter(Boolean) ?? [];
  const fieldRoles = [...new Set((version.fields ?? [])
    .filter((field) => !["computed", "legal_text"].includes(field.type) && field.assignedRole)
    .map((field) => field.assignedRole))];
  const roles = configuredRoles.length ? configuredRoles : fieldRoles.length ? fieldRoles : ["signer"];
  return roles.map((role, index) => ({ id: `signer-${index + 1}`, name: "", email: "", role }));
}

function preparationAssignmentsForVersion(version: PublishedVersion, signers: readonly PreparationSigner[]) {
  return Object.fromEntries((version.fields ?? [])
    .filter((field) => !["computed", "legal_text"].includes(field.type))
    .map((field) => [field.id, signers.find((signer) => signer.role === field.assignedRole)?.id ?? ""]));
}

export function buildPreparationPayload(
  title: string,
  mode: string,
  signers: readonly PreparationSigner[],
  assignments: Readonly<Record<string, string>>,
  signatureLevel: "ses" | "ades" = "ses",
) {
  return {
    title: title.trim(),
    signatureLevel,
    signers: signers.map((signer, index) => ({
      name: signer.name.trim(),
      email: signer.email.trim(),
      role: signer.role.trim(),
      signingOrder: mode === "parallel" ? 1 : index + 1,
    })),
    assignments: Object.entries(assignments).flatMap(([fieldId, signerId]) => {
      const signerIndex = signers.findIndex((signer) => signer.id === signerId);
      return signerIndex < 0 ? [] : [{ fieldId, signerIndex }];
    }),
  };
}

export function DocumentPreparationDialog({ token, templates, onClose, onSent }: {
  token: string;
  templates: TemplateBundles;
  onClose(): void;
  onSent(message: string): void;
}) {
  const published = publishedPreparationOptions(templates);
  const [versionId, setVersionId] = useState(published[0]?.version.id ?? "");
  const selected = published.find((option) => option.version.id === versionId) ?? published[0];
  const [title, setTitle] = useState(selected ? `${selected.template.name} - ${new Date().toISOString().slice(0, 10)}` : "");
  const [signers, setSigners] = useState<PreparationSigner[]>(() => selected ? preparationSignersForVersion(selected.version) : []);
  const [assignments, setAssignments] = useState<Record<string, string>>(() => selected ? preparationAssignmentsForVersion(selected.version, preparationSignersForVersion(selected.version)) : {});
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [importState, setImportState] = useState("");
  const importInput = useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState(false);
  const [signatureLevel, setSignatureLevel] = useState<"ses" | "ades">("ses");
  const [allowedSignatureLevels, setAllowedSignatureLevels] = useState<readonly ("ses" | "ades")[]>(["ses"]);
  const mode = selected?.version.routingPolicy?.mode ?? "single";
  const fields = (selected?.version.fields ?? []).filter((field) => !["computed", "legal_text"].includes(field.type));

  useEffect(() => {
    let active = true;
    void api.billingEntitlements(token).then((entitlements) => {
      if (!active) return;
      const levels: readonly ("ses" | "ades")[] = entitlements.platformFeatureSuperset ? ["ses", "ades"] : entitlements.allowedSignatureLevels?.length ? entitlements.allowedSignatureLevels : ["ses"];
      setAllowedSignatureLevels(levels);
      if (!levels.includes(signatureLevel)) setSignatureLevel("ses");
    }).catch(() => { if (active) setAllowedSignatureLevels(["ses"]); });
    return () => { active = false; };
  }, [token, signatureLevel]);

  const chooseTemplate = (nextVersionId: string) => {
    const next = published.find((option) => option.version.id === nextVersionId);
    setVersionId(nextVersionId);
    if (!next) return;
    setTitle(`${next.template.name} - ${new Date().toISOString().slice(0, 10)}`);
    const nextSigners = preparationSignersForVersion(next.version);
    setSigners(nextSigners);
    setAssignments(preparationAssignmentsForVersion(next.version, nextSigners));
  };
  const updateSigner = (id: string, patch: Partial<PreparationSigner>) => setSigners((current) => current.map((signer) => signer.id === id ? { ...signer, ...patch } : signer));
  const importRecipients = async (file?: File) => {
    if (!file) return;
    setError("");
    setImportState("Reading recipient CSV…");
    try {
      const imported = parseRecipientCsv(await file.text(), signers);
      setSigners(imported);
      setImportState(`Imported ${imported.length} recipient${imported.length === 1 ? "" : "s"} by template role.`);
    } catch (cause) {
      setImportState("");
      setError(cause instanceof Error ? cause.message : "Recipient CSV could not be imported.");
    } finally {
      if (importInput.current) importInput.current.value = "";
    }
  };
  const advance = () => {
    setError("");
    if (step === 1 && (!selected || !title.trim())) return setError("Choose a published template and enter a document title.");
    if (step === 2) {
      if (signers.some((signer) => !signer.name.trim() || !signer.role.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signer.email.trim()))) return setError("Enter a name, valid email, and role for every signer.");
      if (new Set(signers.map((signer) => signer.email.trim().toLowerCase())).size !== signers.length) return setError("Signer email addresses must be unique.");
      if (mode === "single" && signers.length !== 1) return setError("This template allows one signer.");
    }
    if (step === 3) {
      if (fields.some((field) => field.required && !assignments[field.id])) return setError("Assign every required field.");
      if (signers.some((signer) => !Object.values(assignments).includes(signer.id))) return setError("Assign at least one field to every signer.");
    }
    setStep((current) => Math.min(4, current + 1));
  };
  const send = async () => {
    if (!selected) return;
    setSending(true);
    setError("");
    try {
      const payload = buildPreparationPayload(title, mode, signers, assignments, signatureLevel);
      const result = await api.prepareDocument(token, selected.version.id, payload);
      onSent(`Sent ${title.trim()} to ${result.signerCount} signer${result.signerCount === 1 ? "" : "s"}.`);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Document preparation failed.");
    } finally {
      setSending(false);
    }
  };

  return <div className="prepare-backdrop" role="presentation">
    <section className="prepare-dialog" role="dialog" aria-modal="true" aria-labelledby="prepare-title">
      <header className="prepare-header">
        <div><p className="eyebrow">Prepare agreement</p><h2 id="prepare-title">Assign the signing route.</h2></div>
        <button className="prepare-close" type="button" onClick={onClose} aria-label="Close preparation">Close</button>
      </header>
      <ol className="prepare-steps" aria-label="Preparation progress">{["Template", "Signers", "Fields", "Review"].map((label, index) => <li key={label} className={step === index + 1 ? "active" : step > index + 1 ? "complete" : ""}><span>{index + 1}</span>{label}</li>)}</ol>
      <div className="prepare-body">
        {step === 1 && <div className="prepare-panel"><label>Published template<select aria-label="Published template" value={versionId} onChange={(event) => chooseTemplate(event.target.value)}>{published.map((option) => <option key={option.version.id} value={option.version.id}>{option.template.name} · version {option.version.number}</option>)}</select></label><label>Document title<input aria-label="Document title" value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} /></label><label>Signature assurance<select aria-label="Signature assurance" value={signatureLevel} onChange={(event) => setSignatureLevel(event.target.value as "ses" | "ades")}><option value="ses">Simple electronic signature (SES)</option>{allowedSignatureLevels.includes("ades") && <option value="ades">Advanced electronic signature (AdES) · device verification</option>}</select><small>{signatureLevel === "ades" ? "Every signer confirms with Face ID, fingerprint, device PIN, or a passkey. Biometric information stays on the signer's device. This is not a qualified electronic signature." : "Signers authenticate by email code and provide a typed or drawn signature."}</small></label><div className="prepare-note"><strong>{friendlyMode(mode)} route</strong><span>{fields.length} assignable field{fields.length === 1 ? "" : "s"}</span></div></div>}
        {step === 2 && <div className="prepare-panel"><div className="prepare-panel-heading"><div><h3>Who needs to act?</h3><p>{mode === "parallel" ? "All template signers receive the agreement together." : mode === "sequential" ? "Signers follow the order defined by the template." : "This template uses one signer."}</p></div><div className="recipient-import"><label className="button button-light">Import CSV<input ref={importInput} type="file" accept=".csv,text/csv" aria-label="Import recipient CSV" onChange={(event) => void importRecipients(event.target.files?.[0])} /></label><small>Columns: role, name, email</small></div></div>{importState && <p className="recipient-import-status" role="status">{importState}</p>}<div className="signer-stack">{signers.map((signer, index) => <article className="signer-card" key={signer.id}><div className="signer-order"><strong>{mode === "parallel" ? "Together" : `#${index + 1}`}</strong></div><label>Name<input aria-label={`Signer ${index + 1} name`} value={signer.name} onChange={(event) => updateSigner(signer.id, { name: event.target.value })} /></label><label>Email<input aria-label={`Signer ${index + 1} email`} type="email" value={signer.email} onChange={(event) => updateSigner(signer.id, { email: event.target.value })} /></label><label>Role<input aria-label={`Signer ${index + 1} role`} value={signer.role} readOnly /></label></article>)}</div></div>}
        {step === 3 && <div className="prepare-panel"><div className="prepare-panel-heading"><div><h3>Assign every action.</h3><p>Each required field must belong to exactly one signer.</p></div></div><div className="assignment-list">{fields.map((field) => <label key={field.id}><span><strong>{field.label ?? friendlyMode(field.type)}</strong><small>{friendlyMode(field.type)}{field.required ? " · Required" : " · Optional"}</small></span><select aria-label={`Assign ${field.label ?? field.id}`} value={assignments[field.id] ?? ""} onChange={(event) => setAssignments((current) => ({ ...current, [field.id]: event.target.value }))}><option value="">Unassigned</option>{signers.map((signer) => <option key={signer.id} value={signer.id}>{signer.name || signer.email || "Unnamed signer"}</option>)}</select></label>)}</div></div>}
        {step === 4 && selected && <div className="prepare-panel review-panel"><div className="review-document"><p className="eyebrow">Ready to send</p><h3>{title}</h3><span>{selected.template.name} · version {selected.version.number}</span><span>{signatureLevel === "ades" ? "AdES · passkey confirmation required" : "SES"}</span></div><div className="review-route">{signers.map((signer, index) => <article key={signer.id}><strong>{mode === "parallel" ? "Parallel" : `Step ${index + 1}`}</strong><div><h4>{signer.name}</h4><p>{signer.email} · {signer.role}</p><small>{Object.values(assignments).filter((id) => id === signer.id).length} assigned field(s)</small></div></article>)}</div><p className="review-warning">Sending protects this version and emails the first signer.</p></div>}
        {error && <p className="prepare-error" role="alert">{error}</p>}
      </div>
      <footer className="prepare-footer"><button className="button button-light" type="button" onClick={step === 1 ? onClose : () => setStep((current) => current - 1)}>{step === 1 ? "Cancel" : "Back"}</button>{step < 4 ? <button className="button button-dark" type="button" onClick={advance}>Continue</button> : <button className="button button-dark" type="button" disabled={sending} onClick={() => void send()}>{sending ? "Sending…" : "Send for signature"}</button>}</footer>
    </section>
  </div>;
}

function friendlyMode(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}
